/**
 * Применяет данные карточки (history / style / generation) к state генератора.
 *
 * Извлечено из GenerateWidget.tsx для:
 *   1. Тестируемости (без рендера React)
 *   2. Переиспользования между GenerateWidget и Generate.tsx
 *   3. Читаемости — логика тут, UI в компоненте
 *
 * Функция вызывает только переданные actions и не зависит напрямую ни от
 * React, ни от GenerateContext. Все внешние зависимости инжектируются
 * через параметр `deps`.
 */
import { findStickerForCard, takeRestore } from '../../../api/generationStickers'
import type { GenerationCard } from '../../../types/generationCard'
import type { InputsSnapshot } from './inputsSnapshot'
import type { GenerateSettings } from '../hooks/useGenerateSettings'
import { applyCardSettings } from './applyCardSettings'
import { buildOpenVariantPrompt, buildVariantPrompt, substitutePhotoSlots, variantFieldValue } from '../../../utils/variantPrompt'
import { getAttachedStyleCard } from '../../../api/attachedStyleCard'

// ── Публичные интерфейсы ──────────────────────────────────────

/** Минимальный набор actions, которые логика вызывает у генератора. */
export interface GenerateActions {
	setPrompt: (value: string) => void
	resetUploads: () => void
	/** Сбросить только референсы карточки «под капотом» — для card.keepUploads (смена участника мульти-тренда). */
	resetCardRefs?: () => void
	/** Очистка ЗОНЫ РЕЗУЛЬТАТА. Обязательна при применении карточки: результат
	 *  принадлежит ПРОШЛОМУ запросу, а мы настраиваем новый. */
	resetResult?: () => void
	/** Вернуть в зону результата КОНКРЕТНЫЙ результат — возврат по стикеру
	 *  незакрытой генерации. Вызывается ВМЕСТО resetResult. */
	restoreResult?: (payload: { result: string; resultUrls?: string[] | null; requestId?: string | null }) => void
	/** Вернуть входы из снимка стикера — каждый канал на своё место. Есть снимок
	 *  и есть этот action → одиночный путь (getSourceFileFromCard) не нужен. */
	restoreInputs?: (snapshot: InputsSnapshot) => Promise<void> | void
	updateSetting: <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void
	handleImageUpload: (e: { target: { files: File[] } }) => Promise<void> | void
	setIsUploading: (value: boolean) => void
	applyDefaultReferenceVideo: (url: string) => Promise<void> | void
	applyDefaultUploadedVideo: (url: string) => Promise<void> | void
	applyDefaultUploadedImage: (url: string) => Promise<void> | void
	setShowStyleHint: (value: boolean) => void
	/** Демо-переменные (опц., только виджет /bots): загрузить чипы карточки. */
	setVariantCard?: (
		chips: { key: string; label: string; patch: string; userInput?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }[],
		base: string,
		cardId: string,
		initialKey?: string,
		userInput?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null,
		initialUserValues?: string | Record<string, string>,
	) => void
	clearVariantCard?: () => void
}

/** Промпт и демо-переменные карточки — ОДИН код для виджета /bots и страницы /generate (22.10: /generate клал сырую
 *  базу с {variant} и не ставил чипы — по ссылке на инструмент первая генерация уходила без стиля). Вариации есть →
 *  чипы в VariantContext + промпт стартовой вариации (патч, {value} пусто, фото-слоты фразами); нет → обычный промпт и
 *  сброс чипов прошлой карточки, чтобы не залипли. */
export function applyVariantPrompt(
	card: GenerationCard,
	actions: Pick<GenerateActions, 'setPrompt' | 'setVariantCard' | 'clearVariantCard'>,
): void {
	const kind = card.kind === 'video' ? 'video' : 'image'
	if (card.demoVariants?.length && actions.setVariantCard) {
		const chips = card.demoVariants.map((v) => ({ key: v.key, label: v.label, patch: v.prompt_patch, isOpen: v.is_open, placeholder: v.placeholder, afterUrl: v.after_url, valueType: v.value_type, minValue: v.min_value, maxValue: v.max_value, userInput: v.user_input ?? null }))
		// Та же карточка уже в пине (F5 по ссылке, окно «Приложения»): стартовый стиль и значения берём из него — пин
		// хранит их ради F5 (22.10: без этого поле показывало значение, а промпт уходил пустым или сырым).
		const pin = getAttachedStyleCard()
		const same = !!pin?.id && (pin.id === card.id || `popular-${pin.id}` === card.id) ? pin : null
		const wanted = card.activeVariantKey ?? same?.activeVariantKey
		const initialKey = wanted && chips.some((c) => c.key === wanted) ? wanted : chips[0]?.key
		const initial = chips.find((c) => c.key === initialKey)
		const base = card.prompt || ''
		const values = same ? (same.userValues ?? (same.userValue ? { __card__: same.userValue } : {})) : {}
		const prompt = initial?.isOpen
			? buildOpenVariantPrompt(base, initial.patch ?? '', same?.customValue ?? '')
			: buildVariantPrompt(base, initial?.patch ?? '').replace(/\{value\}/g, same ? variantFieldValue({ ...same, demoVariants: card.demoVariants }, initialKey) : '')
		actions.setPrompt(substitutePhotoSlots(prompt, kind))
		actions.setVariantCard(chips, base, card.id, initialKey, card.user_input ?? null, values)
		return
	}
	if (card.prompt) actions.setPrompt(substitutePhotoSlots(card.prompt, kind))
	actions.clearVariantCard?.()
}

/** Информация о выбранной модели, влияющая на обработку стилевых карточек. */
export interface SelectedModelInfo {
	requiresVideo?: boolean
}

function isVideoUrl(url: string): boolean {
	const ext = url.split('?')[0].split('.').pop()?.toLowerCase()
	return ['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext || '')
}

/** Зависимости которые нужны для получения исходника. */
export interface ApplyCardDeps {
	/** Вытаскивает File из карточки (из IndexedDB или base64-fallback). */
	getSourceFileFromCard: (card: GenerationCard) => Promise<File | null>
}

// ── Результат применения — для тестирования и отладки ────────────

export type ApplyCardResult =
	| { kind: 'audio'; appliedAudioSettings: boolean }
	| { kind: 'file-applied'; file: File }
	| { kind: 'inputs-restored' }
	| { kind: 'style-default-reference-video'; url: string }
	| { kind: 'style-default-uploaded-video'; url: string }
	| { kind: 'style-default-uploaded-image'; url: string }
	| { kind: 'style-hint-shown' }
	| { kind: 'no-source' }
	| { kind: 'error'; error: unknown }

// ── Главная функция ──────────────────────────────────────────────

/**
 * Применяет карточку к state генератора.
 *
 * Порядок операций:
 *   1. Если у карточки есть source image → показываем loading
 *   2. resetUploads() + resetResult() — чистим предыдущие загрузки и зону
 *      результата (кроме audio)
 *   3. setPrompt(card.prompt)
 *   4. updateSetting × для каждой настройки которая есть в card.settings
 *   5. Для audio — применяем audio-specific настройки и заканчиваем
 *   6. Для image/video — пробуем загрузить source file
 *   7. Если source file есть → handleImageUpload
 *   8. Если нет, и это style — пробуем default media или показываем hint
 *
 * Возвращает ApplyCardResult — это полезно для тестов и debug'а.
 */
export async function applyCardToGenerate(
	card: GenerationCard,
	actions: GenerateActions,
	selectedModel: SelectedModelInfo | null,
	deps: ApplyCardDeps,
): Promise<ApplyCardResult> {
	const hasSource = !!(card.sourceImageDbKey || card.sourceImageUrl)

	// Показываем загрузку сразу — до resetUploads, чтобы слот не мигал пустым
	if (hasSource) actions.setIsUploading(true)

	// Смена участника мульти-тренда с тем же числом полей (23.10): фото пользователя остаются, заново подставляются
	// только референсы новой карточки (resetCardRefs бьёт эпоху — эффекты tool-режима их переподставят).
	if (card.keepUploads && actions.resetCardRefs) actions.resetCardRefs()
	else actions.resetUploads()
	// Зону результата чистим вместе с загрузками. Иначе «Воссоздать» на той же
	// модели оставлял в ней СТАРЫЙ кадр — тот самый, с которого юзер и нажал
	// (жалоба владельца 2026-07-25): новая генерация уже шла, а на экране
	// висел прошлый результат. При смене модели это делал onModelReset —
	// теперь поведение одинаковое независимо от того, менялась ли модель.
	//
	// Исключение — возврат по стикеру незакрытой генерации: там человек пришёл
	// СМОТРЕТЬ свой результат, и очистка стирала бы ровно то, за чем он шёл.
	// Заявка адресная (совпадает карточка), одноразовая и живёт минуту, поэтому
	// «Воссоздать» её не подхватит.
	// Клик по стикеру оставляет адресную заявку. Пришли обычным путём (меню,
	// плитка) — заявки нет, но незакрытая работа по карточке может быть: тогда
	// берём её из самого стикера. Он держится до клика или крестика, поэтому
	// «закрыл крестиком» остаётся закрытым и ничего не всплывает обратно.
	const explicit = takeRestore(card.id)
	// Смена версии мульти-тренда с сохранением фото (keepUploads, 23.10): незакрытая работа по версии не всплывает —
	// иначе её входы легли бы поверх оставленных фото (дубли в запросе). Адресная заявка по клику на стикер главнее.
	const pending = explicit || card.keepUploads ? null : findStickerForCard(card.id)
	const restore = explicit ?? (pending
		? {
			cardId: card.id,
			resultUrl: pending.status === 'done' ? pending.resultUrl : undefined,
			resultUrls: pending.status === 'done' ? pending.resultUrls : undefined,
			requestId: pending.status === 'done' ? pending.requestId : undefined,
			sourceImageDbKey: pending.sourceImageDbKey,
			inputs: pending.inputs,
			at: pending.startedAt,
		}
		: null)
	if (restore?.resultUrl && actions.restoreResult) {
		actions.restoreResult({
			result: restore.resultUrl,
			resultUrls: restore.resultUrls,
			requestId: restore.requestId,
		})
	} else {
		// Идущая генерация: результата ещё нет — зону чистим как обычно,
		// прелоадер в ней рисует сам инструмент по globalGen.status.
		actions.resetResult?.()
	}

	// Демо-переменные карточки: чипы + первая вариация в промпт (мгновенно; последующая смена вариации анимируется
	// через VariantContext), иначе обычный промпт и сброс переменных — общий помощник с /generate.
	applyVariantPrompt(card, actions)

	// Базовые + расширенные настройки (image/video/audio пресет карточки)
	if (card.settings) applyCardSettings(card.settings, actions.updateSetting)

	// Видео-карточка с фото-слотами: фото уходят референсами → Seedance сразу
	// в режим «Референсы» (в «Кадрах» payload референсы игнорирует).
	if (card.kind === 'video' && (card.photo_slots?.length ?? 0) >= 2) {
		actions.updateSetting('seedanceMode', 'references')
	}

	// Аудио-специфика (Suno карточки)
	if (card.kind === 'audio') {
		let appliedAudioSettings = false
		if (card.audioTitle !== undefined) {
			actions.updateSetting('audioTitle', card.audioTitle)
			appliedAudioSettings = true
		}
		if (card.audioStyle !== undefined) {
			actions.updateSetting('audioStyle', card.audioStyle)
			appliedAudioSettings = true
		}
		if (card.audioInstrumental !== undefined) {
			actions.updateSetting('audioInstrumental', card.audioInstrumental)
			appliedAudioSettings = true
		}
		if (card.audioVocalGender !== undefined) {
			actions.updateSetting('audioVocalGender', card.audioVocalGender)
			appliedAudioSettings = true
		}
		if (card.audioCustomMode !== undefined) {
			actions.updateSetting('audioCustomMode', card.audioCustomMode)
			appliedAudioSettings = true
		}
		if (hasSource) actions.setIsUploading(false)
		return { kind: 'audio', appliedAudioSettings }
	}

	// Снимок входов стикера: каждый канал возвращается туда, где лежал — слоты,
	// второе фото, видео, аудио. Одиночный путь ниже остаётся для стикеров без
	// снимка (поставлены до его появления) и для «Воссоздать» из истории.
	if (restore?.inputs && actions.restoreInputs) {
		try {
			await actions.restoreInputs(restore.inputs)
			return { kind: 'inputs-restored' }
		} catch (error) {
			if (hasSource) actions.setIsUploading(false)
			return { kind: 'error', error }
		}
	}

	// Пробует IndexedDB (новые записи), fallback на base64 sourceImageUrl (старые/стили).
	// Возврат по стикеру: берём фото ЮЗЕРА из его генерации, а не демо-исходник
	// карточки — человек возвращается к своей работе, слот должен быть занят ею.
	const cardForSource: GenerationCard = restore?.sourceImageDbKey
		? { ...card, sourceImageDbKey: restore.sourceImageDbKey, sourceImageUrl: undefined }
		: card
	try {
		const file = await deps.getSourceFileFromCard(cardForSource)
		if (file) {
			await actions.handleImageUpload({ target: { files: [file] } })
			return { kind: 'file-applied', file }
		}

		// Source нет — для style карточек пробуем default media
		if (hasSource) actions.setIsUploading(false)

		if (card.type === 'style') {
			// Явный «Видео-референс (под капотом)» из админки СИЛЬНЕЕ старого
			// канала default_media_url категории: иначе старый файл (бывает
			// битый/устаревший) занимал слот, и GTM-подстановка референса в
			// уже занятый слот не срабатывала.
			// С 19.09 ссылки у клиента может не быть вовсе (её убрали из публичной
			// витрины) — наличие референса определяет флаг, иначе старый битый
			// default_media_url снова занимал бы слот и глушил референс.
			const hasAdminVideoRef = !!(card.referenceVideoUrl || card.hasRefVideo)
			const defaultMedia =
				hasAdminVideoRef && card.defaultMediaUrl && isVideoUrl(card.defaultMediaUrl)
					? undefined
					: card.defaultMediaUrl
			if (defaultMedia) {
				const isVideo = isVideoUrl(defaultMedia)
				if (isVideo) {
					if (selectedModel?.requiresVideo) {
						// Motion Control — видео в uploadedVideo (слот 2)
						await actions.applyDefaultUploadedVideo(defaultMedia)
						return { kind: 'style-default-uploaded-video', url: defaultMedia }
					} else {
						// Остальные видео — в referenceVideo (слот 3)
						await actions.applyDefaultReferenceVideo(defaultMedia)
						return { kind: 'style-default-reference-video', url: defaultMedia }
					}
				} else {
					// Изображение — в uploadedImage (слот 1)
					await actions.applyDefaultUploadedImage(defaultMedia)
					return { kind: 'style-default-uploaded-image', url: defaultMedia }
				}
			} else {
				actions.setShowStyleHint(true)
				return { kind: 'style-hint-shown' }
			}
		}

		return { kind: 'no-source' }
	} catch (error) {
		if (hasSource) actions.setIsUploading(false)
		return { kind: 'error', error }
	}
}
